import AudioOffIcon from "@/assets/icons/ui/audio-off.svg";
import AudioOnIcon from "@/assets/icons/ui/audio-on.svg";
import Image from "next/image";
import { useMemo, useRef } from "react";
import { convertFilePathToUrl } from "../../util/convertFilePathToUrl";
import ImageInputIcon from "./assets/image-input-icon.svg";
import MusicInputIcon from "./assets/music-input-icon.svg";
import TextInputIcon from "./assets/text-input-icon.svg";
import VideoInputIcon from "./assets/video-input-icon.svg";

export interface CommunityThreadConfig {
  id: string;
  title: string;
  imageUrl: string;
  latestResultVideoUrl: string;
  options: {
    aspectRatio: "9:16" | "16:9";
    knowledgeId: string;
  };
  tags: {
    duration: number;
    inputs: ("video" | "text" | "music" | "image")[];
  };
}

interface IProps {
  thread: CommunityThreadConfig;
  isMuted: boolean;
  onToggleMuted: () => void;
}

const formatDuration = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

const INPUT_ICON_MAP = {
  image: ImageInputIcon,
  music: MusicInputIcon,
  text: TextInputIcon,
  video: VideoInputIcon,
};

export const CommunityThreadItem = (props: IProps) => {
  const { thread, isMuted, onToggleMuted } = props;
  const videoRef = useRef<HTMLVideoElement>(null);

  const knowledgeId = thread.options?.knowledgeId || "";

  const aspectRatio = thread.options?.aspectRatio || "16:9";

  const duration = useMemo(() => {
    return thread.tags?.duration ?? 0;
  }, [thread.tags?.duration]);

  const inputs = useMemo(() => {
    return thread.tags?.inputs || [];
  }, [thread.tags?.inputs]);

  const handleMouseEnter = () => {
    if (thread.latestResultVideoUrl && videoRef.current) {
      videoRef.current.play().catch((err: unknown) => {
        // Handle playback errors (e.g., interrupted by pause)
        console.warn("Video playback failed:", err);
      });
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="group relative w-full transition-all duration-200 hover:-translate-y-1.5 hover:z-[5] hover:scale-105 origin-top animate-zoom-in-appear"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="relative w-full overflow-hidden bg-background-elevated group-hover:rounded-t-lg"
        style={{ aspectRatio: aspectRatio.replace(":", "/") }}
      >
        <Image
          src={convertFilePathToUrl(thread.imageUrl, { level: "medium" })}
          alt={thread.title || "Thread thumbnail"}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 50vw, (max-width: 1280px) 25vw, 20vw"
          loading="lazy"
          unoptimized
        />

        {thread.latestResultVideoUrl && (
          <video
            ref={videoRef}
            src={convertFilePathToUrl(thread.latestResultVideoUrl)}
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            muted={isMuted}
            loop
            playsInline
          />
        )}

        {inputs.length > 0 && (
          <div className="absolute left-2 bottom-2 flex items-center justify-center gap-2 px-2 py-0.5 rounded-sm bg-[rgba(32,32,32,0.12)] backdrop-blur-lg text-text-primary z-[2]">
            <span>Input: </span>
            <span className="text-xs font-medium capitalize">{inputs[0]}</span>
          </div>
        )}

        <div
          className="absolute right-2 bottom-3 hidden group-hover:flex items-center gap-1.5 text-white text-xs font-medium leading-normal select-none z-[2]"
          style={{ textShadow: "0 0 4px rgba(0, 0, 0, 0.48)" }}
        >
          <div
            className="flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95 transition-transform pointer-events-auto"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleMuted();
            }}
          >
            {isMuted ? (
              <AudioOffIcon className="w-5 h-5 " />
            ) : (
              <AudioOnIcon className="w-5 h-5" />
            )}
          </div>
          <span className="pointer-events-none">
            {formatDuration(duration)}
          </span>
        </div>
      </div>

      {/* Unified Hover Overlay (Gradient Blur + Footer Content) */}
      <div className="opacity-0 group-hover:opacity-100 absolute left-0 right-0 z-[1] bg-black/20 backdrop-blur-[60px] transition-opacity duration-200 pointer-events-none group-hover:rounded-b-lg">
        <div className="flex flex-col gap-1 p-3 w-full pointer-events-auto z-[2]">
          {/* {knowledgeId && KNOWLEDGE_BASE_MAP[knowledgeId] && (
            <div className="inline-flex w-fit items-center gap-1 bg-white/10 backdrop-blur-xl rounded-sm px-1.5 py-1">
              {KNOWLEDGE_BASE_MAP[knowledgeId].icon && (
                <span className="flex items-center justify-center w-4 h-4 text-white">
                  {KNOWLEDGE_BASE_MAP[knowledgeId].icon}
                </span>
              )}
              <span className="text-xs font-medium text-white">
                {KNOWLEDGE_BASE_MAP[knowledgeId].text}
              </span>
            </div>
          )} */}
          <div className="text-sm font-semibold text-white line-clamp-2 leading-normal">
            {thread.title}
          </div>
        </div>
      </div>
    </div>
  );
};
